// Client-side YouTube upload via Google OAuth (GIS) + resumable upload.
//
// Why client-side: Netlify functions cap request bodies at ~6MB and time out
// in 10-26s, so video bytes can't be proxied through them. The browser uploads
// straight to YouTube; the backend only stores the returned video id.
//
// Requires a Google OAuth Web Client id in REACT_APP_GOOGLE_CLIENT_ID, with the
// YouTube Data API v3 enabled and the app's origin listed as an authorized
// JavaScript origin. The signed-in Google account must own the target channel.

const GIS_SRC = 'https://accounts.google.com/gsi/client'
const SCOPE = 'https://www.googleapis.com/auth/youtube.upload'
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID
// Account to upload as — used as the OAuth login hint so the prompt targets it.
const ACCOUNT_HINT = process.env.REACT_APP_YOUTUBE_ACCOUNT

export const hasYoutubeConfig = () => !!CLIENT_ID

let tokenClient
let cachedToken
let tokenExpiry = 0

const loadScript = src =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve()
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Failed to load Google sign-in script'))
    document.head.appendChild(s)
  })

const getAccessToken = async () => {
  if (cachedToken && Date.now() < tokenExpiry - 60000) return cachedToken
  if (!CLIENT_ID)
    throw new Error('Missing REACT_APP_GOOGLE_CLIENT_ID (see .env)')
  await loadScript(GIS_SRC)

  return new Promise((resolve, reject) => {
    const callback = resp => {
      if (resp.error) return reject(new Error(resp.error))
      cachedToken = resp.access_token
      tokenExpiry = Date.now() + (resp.expires_in || 3600) * 1000
      resolve(cachedToken)
    }
    if (!tokenClient) {
      tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPE,
        hint: ACCOUNT_HINT,
        callback,
      })
    } else {
      tokenClient.callback = callback
    }
    // Pass the hint per-request too, so the prompt targets the desired account
    // even if a different Google account is already signed in.
    tokenClient.requestAccessToken(ACCOUNT_HINT ? { hint: ACCOUNT_HINT } : {})
  })
}

// Uploads a video file to YouTube and resolves with the new video id.
// opts: { title, description, privacyStatus, onProgress(loaded, total) }
export const uploadVideo = async (file, opts = {}) => {
  const token = await getAccessToken()
  const meta = {
    snippet: {
      title: opts.title || file.name,
      description: opts.description || '',
    },
    status: { privacyStatus: opts.privacyStatus || 'unlisted' },
  }

  // 1) Open a resumable upload session.
  const start = await fetch(
    'https://www.googleapis.com/upload/youtube/v3/videos?uploadType=resumable&part=snippet,status',
    {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
        'X-Upload-Content-Type': file.type || 'video/mp4',
        'X-Upload-Content-Length': file.size,
      },
      body: JSON.stringify(meta),
    }
  )
  if (!start.ok) {
    const t = await start.text()
    if (/youtubeSignupRequired/.test(t))
      throw new Error(
        'The signed-in Google account has no YouTube channel. Create a channel ' +
          'for it at youtube.com (or sign in with the account that owns the ' +
          'target channel), then try again.'
      )
    throw new Error(`Could not start upload (${start.status}): ${t}`)
  }
  const location = start.headers.get('location') || start.headers.get('Location')
  if (!location) throw new Error('No upload URL returned by YouTube')

  // 2) Upload the bytes (XHR so we get upload progress events).
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', location, true)
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4')
    xhr.upload.onprogress = e => {
      if (opts.onProgress && e.lengthComputable) opts.onProgress(e.loaded, e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText).id)
        } catch (e) {
          reject(new Error('Unexpected upload response from YouTube'))
        }
      } else {
        reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText}`))
      }
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.send(file)
  })
}
